import React, { useCallback, useMemo, useRef, useEffect } from "react";
import TicketDescriptionBox from "./TicketDescriptionBox";
import {
  useUpdateTicketMutation,
  useUploadHelpdeskAttachmentMutation,
  useCreateTicketAttachmentMutation,
} from "../../../../../../../../../redux/services/helpdeskApi";
import { useOrgUsersList } from "../../../../../../../../../hooks/useOrgUsersList";
import { PermissionDisabledWrapper } from "../../../../PermissionDisabledWrapper";
import { useHasHelpdeskUpdateAccess } from "../../../../../utils/helpdeskPermissions";

interface TicketAttachmentShape {
  s3_key: string;
  s3_url: string;
  file_name: string;
  content_type: string;
  size_in_bytes: number;
}

interface TicketDescriptionProps {
  ticketId: string;
  description?: string;
  existingAttachments?: TicketAttachmentShape[];
  ticketCategory?: string;
}

const DEBOUNCE_MS = 800;

const isHtmlContentEmpty = (html: string | undefined | null): boolean => {
  if (!html) return true;
  if (html === "<p></p>") return true;
  if (html?.includes("<img")) return false;
  const textOnly = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/g, " ")
    .replace(/\u00A0/g, " ")
    .trim();
  return textOnly === "";
};

const normalizeDescription = (desc: string | undefined | null): string =>
  isHtmlContentEmpty(desc) ? "" : (desc ?? "");

const TicketDescription: React.FC<TicketDescriptionProps> = ({
  ticketId,
  description,
  ticketCategory,
}) => {
  const [updateTicket] = useUpdateTicketMutation();
  const [uploadHelpdeskAttachment] = useUploadHelpdeskAttachmentMutation();
  const [createTicketAttachment] = useCreateTicketAttachmentMutation();
  const canUpdate = useHasHelpdeskUpdateAccess(ticketCategory);
  console.log("ticketCategory", ticketCategory);

  const { activeUsers } = useOrgUsersList();

  const saveDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pendingDescriptionHtmlRef = useRef<string | null>(null);
  const taggedUserIdsRef = useRef<string[]>([]);

  const mentionUsers = useMemo(
    () =>
      activeUsers.map((user) => ({
        _id: user._id,
        name: user.name,
        email: user?.email ?? "",
        profile_picture: null,
      })),
    [activeUsers]
  );

  const handleTaggedUsersChange = useCallback((taggedUsers: string[]) => {
    taggedUserIdsRef.current = taggedUsers;
  }, []);

  const normalizedDescription = normalizeDescription(description);

  const saveDescriptionIfChanged = useCallback(() => {
    const nextDescriptionHtml = pendingDescriptionHtmlRef.current;
    pendingDescriptionHtmlRef.current = null;
    if (nextDescriptionHtml === null) return;
    if (nextDescriptionHtml === normalizedDescription) return;
    updateTicket({
      ticketId,
      description: nextDescriptionHtml,
      tagged_user_ids: taggedUserIdsRef.current,
    });
  }, [ticketId, updateTicket, normalizedDescription]);

  const flushPendingSave = useCallback(() => {
    if (saveDebounceTimerRef.current) {
      clearTimeout(saveDebounceTimerRef.current);
      saveDebounceTimerRef.current = null;
    }
    saveDescriptionIfChanged();
  }, [saveDescriptionIfChanged]);

  const handleDescriptionChange = useCallback(
    (html: string) => {
      pendingDescriptionHtmlRef.current = html;
      if (saveDebounceTimerRef.current)
        clearTimeout(saveDebounceTimerRef.current);

      saveDebounceTimerRef.current = setTimeout(() => {
        saveDebounceTimerRef.current = null;
        saveDescriptionIfChanged();
      }, DEBOUNCE_MS);
    },
    [saveDescriptionIfChanged]
  );

  const handleDescriptionSave = useCallback(
    (html: string) => {
      pendingDescriptionHtmlRef.current = html;
      flushPendingSave();
    },
    [flushPendingSave]
  );

  useEffect(() => {
    return () => flushPendingSave();
  }, [flushPendingSave]);

  const handleImageInsert = useCallback(
    async (file: File, onUrlReplace: (s3Url: string) => void) => {
      try {
        const response = await uploadHelpdeskAttachment({
          attachment: file,
          source: "conversation",
        });

        if (
          response &&
          typeof response === "object" &&
          "data" in response &&
          response.data
        ) {
          const apiResponse = Array.isArray(response.data)
            ? response.data[0]
            : response.data;

          if (apiResponse?.s3_url) {
            onUrlReplace(apiResponse.s3_url);
            await createTicketAttachment({
              ticket_id: ticketId,
              s3_key: apiResponse.s3_key,
              file_name: apiResponse.file_name || file.name,
              content_type: apiResponse.content_type || file.type,
              size_in_bytes: apiResponse.size_in_bytes || file?.size,
              s3_url: apiResponse.s3_url,
            });
          }
        }
      } catch {
        // upload failed silently — base64 remains in the description
      }
    },
    [uploadHelpdeskAttachment, createTicketAttachment, ticketId]
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col min-w-0">
      <div className="flex-1 basis-0 flex flex-col rounded-lg bg-pure-white dark:bg-pure-white-dk ">
        <PermissionDisabledWrapper
          disabled={!canUpdate}
          className="flex-1 min-h-0 flex flex-col"
          innerClassName="flex-1 min-h-0 flex flex-col"
        >
          <TicketDescriptionBox
            initialContent={normalizedDescription}
            onSave={handleDescriptionSave}
            onChange={handleDescriptionChange}
            onImageInsert={handleImageInsert}
            mentionActiveUsersList={mentionUsers}
            setTaggedUsers={handleTaggedUsersChange}
            ticketCategory={ticketCategory}
          />
        </PermissionDisabledWrapper>
      </div>
    </div>
  );
};

export default TicketDescription;
